<template>
  <div class="modal-repositories-confirm">
    <div class="modal-repositories-confirm__text">
      Are you sure you want to {{ actionType }}
      <span class="modal-repositories-confirm__name">
        {{ displayName }}
      </span>
      repository?
    </div>
    <div class="modal-repositories-confirm__actions">
      <UICheckbox
        v-model="enableAction"
        :disabled="processing"
        :label="`Please check to confirm ${actionType}`"
        :data-refid="`disable${label}`"
      />
      <UICheckbox
        v-if="enableAction && showExportOption"
        v-model="exportBefore"
        :disabled="processing"
        :label="`Export before ${actionType}`"
        :data-refid="`exportBefore${label}`"
      />
    </div>
    <div class="modal-repositories-confirm__buttons">
      <UIButton
        :disabled="processing"
        label="Cancel"
        variant="secondary"
        fill="outlined"
        @click="handleCancel"
      />
      <UIButton
        v-bind="{ disabled }"
        :loading="processing"
        :label
        variant="danger"
        @click="handleConfirm"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import { Repository } from '@types'

import { NOTIFICATION_DELAY } from '@/const'

import { handleCatchedError } from '@/helpers/common'

import { useNotifications } from '@/plugins/notification'

import { useRepositoriesStore } from '@/store/repositories/index'
import { useRepositoriesCollaboratorsStore } from '@/store/repositories/collaborators'
import { useUserStore } from '@/store/user'

import { UIButton } from '@ui/buttons'
import { UICheckbox } from '@ui/controllers'

type Props = {
  repository: Repository
  actionType: 'delete' | 'unlink'
}

type Emits = {
  cancel: []
  export: [id: string, name: string, resolve: () => void, reject: () => void]
  confirmed: [id: string]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const repositoriesStore = useRepositoriesStore()
const repositoriesCollaboratorsStore = useRepositoriesCollaboratorsStore()
const userStore = useUserStore()

const { success } = useNotifications()

const enableAction = ref(false)
const exportBefore = ref(true)

const processing = ref(false)

const label = computed(() => {
  return props.actionType === 'delete' ? 'Delete' : 'Unlink'
})

const disabled = computed(() => processing.value || !enableAction.value)

const displayName = computed(() => props.repository.name)
const repositoryId = computed(() => props.repository.id)

const showExportOption = computed(() => props.actionType === 'delete')

const handleCancel = () => {
  emit('cancel')
}

const handleConfirm = async () => {
  try {
    processing.value = true
    if (exportBefore.value && showExportOption.value) {
      await new Promise((resolve, reject) => {
        emit(
          'export',
          repositoryId.value,
          displayName.value,
          () => resolve(undefined),
          () => reject(),
        )
      })
    }
    
    if (props.actionType === 'delete') {
      await repositoriesStore.destroy(repositoryId.value)
      await success(
        { message: `${displayName.value} repository deleted` },
        NOTIFICATION_DELAY,
      )
    } else {
      // Unlink
      const userId = userStore.getUserId || ''
      const userEmail = userStore.getEmail || ''
      await repositoriesCollaboratorsStore.destroy(
        userEmail,
        userId,
        repositoryId.value,
      )
      repositoriesStore.remove(repositoryId.value)
      await success(
        { message: `Successfully unlinked from ${displayName.value} repository` },
        NOTIFICATION_DELAY,
      )
    }
    emit('confirmed', repositoryId.value)
  } catch (e) {
    handleCatchedError(e as string, { id: repositoryId.value })
    processing.value = false
    emit('cancel')
  }
}
</script>

<style>
.modal-repositories-confirm {
  @apply absolute inset-0;
  @apply flex flex-col items-center;
  @apply pt-16 px-4 gap-4;
  @apply bg-body/90;
  @apply text-sm text-common-text;

  &__text {
    @apply text-center;
  }

  &__name {
    @apply text-primary-active;
  }

  &__actions {
    @apply flex flex-col;
    @apply gap-y-2;
  }

  &__buttons {
    @apply flex items-center;
    @apply gap-x-2;
  }
}
</style>
